import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../state/AuthContext';
import { useScopedLocalStorage } from '../hooks/useScopedLocalStorage';

// New design system components
import Card from '../components/common/Card.jsx';
import Table, { UtilBar } from '../components/common/Table.jsx';
import Tabs from '../components/common/Tabs.jsx';
import KpiRow from '../components/optimization/KpiRow.jsx';
import TopActionsCard from '../components/optimization/TopActionsCard.jsx';
import AIModal from '../components/optimization/AIModal.jsx';
import { BadgeSkeleton } from '../components/optimization/Skeletons.jsx';
import { CostCell, RecommendedCostCell, SavingCell, RecCell, OsCell, StatusCell, IdCell } from '../components/optimization/RecCells.jsx';

import MultiSelect from '../components/common/MultiSelect.jsx';

// Chart components
import LineChart from '../components/charts/LineChart.jsx';
import VerticalBarChart from '../components/charts/VerticalBarChart.jsx';
import HorizontalBarChart from '../components/charts/HorizontalBarChart.jsx';
import DonutChart from '../components/charts/DonutChart.jsx';

// Services and utilities
import AzureOptimizationService from '../services/AzureOptimizationService';
import RecommendationApiService from '../services/RecommendationApiService';
import InventoryApiService from '../services/InventoryApiService';
import SubscriptionApiService from '../services/SubscriptionApiService';
import APIConstants from '../services/APIConstants';
import DebugService from '../services/DebugService';

// Helper functions and constants
import {
  parseCost, parseSaving, formatSavingStr,
  buildBaseCostKpis, exportCSV, buildGroupedCostDonut, buildExportFilename, ADD_NEW_ACCOUNT,
  STORAGE_KEYS, SUBSCRIPTION_STATUS, TRIAL_SUBSCRIPTION_DATA_LIMIT,
  setScopedItem
} from '../components/common/Helper';

// Legacy components (preserved for subscription flow)
import { Button, Typography, Box } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import SnackbarNotification from '../components/common/SnackbarNotification';
import ConfirmModal from '../components/common/ConfirmModal';

// This component is dedicated to Virtual Machines only.
// Managed Disks / Blob Storage / VM Scale Set / Container Instance /
// Public IP / Web App / Static Web App are handled elsewhere.
const RESOURCE_API_TYPE = 'VM';

// ── Azure Recommendation Type Resolver ─────────────────────────────────
const resolveAzureRec = (recType) => {
  const displayVal = recType ?? '';
  const t = displayVal.toUpperCase().trim();

  let recPillClass = 'rec-rs';
  if (t === 'OPTIMAL' || t === 'OPTIMIZED' || t === 'NONE' || t === 'NA' || t === '') {
    recPillClass = 'rec-ok';
  } else if (t === 'TERMINATE' || t === 'DELETE' || t === 'IDLE' || t === 'RELEASE' || t === 'UNATTACHED') {
    recPillClass = 'rec-id';
  } else if (t === 'RIGHTSIZE' || t === 'DOWNSIZE' || t === 'MODIFY') {
    recPillClass = 'rec-rs';
  } else if (t === 'SCALE-UP' || t === 'UPGRADE') {
    recPillClass = 'rec-warn';
  } else if (t === 'BUY-RI' || t === 'RESERVED') {
    recPillClass = 'rec-buy';
  }

  return {
    recType: displayVal,
    recPillClass,
    recLabel: displayVal !== t ? t : ''
  };
};

// ── Recommendation display helper ───────────────────────────────
const buildRecommendationDisplay = (recType, recommendedSize) => {
  const type = (recType ?? '').toString().trim();
  const size = (recommendedSize ?? '').toString().trim();
  if (!type) return 'NA';
  if (!size || size.toUpperCase() === 'NA') return type;
  return `${type} -> ${size}`;
};

// ── VM Row Mappers ───────────────────────────────────────────
const mapCombinedRows = (data) =>
  data.map((item) => {
    const current = parseCost(item.currentMonthlyPriceValue ?? item.currentVMMonthlyPriceValue ?? 0);
    const recommended = parseCost(item.recommendedVMMonthlyPriceValue ?? item.recommendedMonthlyPriceValue ?? 0);
    const saving = (item.savingCost && item.savingCost > 0) ? parseCost(item.savingCost) : ((item.savingAmount && item.savingAmount > 0) ? parseCost(item.savingAmount) : (current - recommended));
    const rec = resolveAzureRec(item.recommendationType);

    return {
      id: item.id,
      name: item.vmName || item.name || 'Unknown',
      region: item.location,
      type: 'VM',
      status: item.vmStatus || 'Unknown',
      os: item.operatingSystem || '—',
      diskSizeGB: item.diskSizeGB ?? null,
      cpu: parseFloat(item.averageCpuValue ?? item.averageCPUValue) || 0,
      peakCpu: parseFloat(item.peakCpuValue ?? item.peakCPUValue) || 0,
      mem: item.memoryGB ?? item.ramGB ?? null,
      cost: `$${current.toFixed(2)}`,
      recommendedCost: `$${recommended.toFixed(2)}`,
      saving: formatSavingStr(saving),
      savingValue: saving,
      costValue: current,
      recommendedCostValue: recommended,
      recType: rec.recType,
      recPillClass: rec.recPillClass,
      recommendationRemark: item.remark || buildRecommendationDisplay(item.recommendationType, item.recommendedVMSize),
      recLabel: item.recommendationDetail || rec.recLabel,
      actionLabel: 'Assist with AI',
      actionClass: 'mini-btn ai-assist',
      resourceGroup: item.resourceGroup,
      sku: item.currentVMSize || item.vmSize || item.skuName,
      utilization: item.averageCPUValue || item.peakCPUValue,
      tags: item.Tags || item.tags || [],
      checked: false,
    };
  });

const mapCustomRows = (data) =>
  data.map((item) => {
    const current = parseCost(item.currentMonthlyPriceValue ?? item.currentVMMonthlyPriceValue ?? 0);
    const recommended = parseCost(item.recommendedVMMonthlyPriceValue ?? item.recommendedMonthlyPriceValue ?? 0);
    const saving = (item.savingCost && item.savingCost > 0) ? parseCost(item.savingCost) : ((item.savingAmount && item.savingAmount > 0) ? parseCost(item.savingAmount) : (current - recommended));
    const rec = resolveAzureRec(item.recommendationType);

    return {
      id: item.id,
      name: item.name || item.vmName || 'Unknown',
      region: item.location || 'Unknown',
      type: 'VM',
      status: item.vmStatus || 'Unknown',
      os: item.osType || '—',
      diskSizeGB: item.diskSizeGB ?? null,
      cpu: parseFloat(item.averageCpuValue ?? item.averageCPUValue) || 0,
      peakCpu: parseFloat(item.peakCpuValue ?? item.peakCPUValue) || 0,
      mem: item.memoryGB ?? item.ramGB ?? null,
      cost: `$${current.toFixed(2)}`,
      recommendedCost: `$${recommended.toFixed(2)}`,
      saving: formatSavingStr(saving),
      savingValue: saving,
      costValue: current,
      recommendedCostValue: recommended,
      recType: rec.recType,
      recPillClass: rec.recPillClass,
      recommendationRemark: item.remark || buildRecommendationDisplay(item.recommendationType, item.recommendedVMSize ?? item.recommendedSku),
      recLabel: item.recommendationDetail || rec.recLabel,
      actionLabel: 'Assist with AI',
      actionClass: 'mini-btn ai-assist',
      resourceGroup: item.resourceGroup,
      sku: item.currentVMSize || item.vmSize || item.skuName,
      utilization: item.averageCpuValue || item.averageCPUValue || item.peakCpuValue || item.peakCPUValue,
      tags: item.Tags || item.tags || [],
      checked: false,
    };
  });

const mapAzureRows = (data) =>
  data.map((item) => {
    const current = parseCost(item.currentMonthlyPriceValue ?? item.currentVMMonthlyPriceValue ?? 0);
    const recommended = parseCost(item.recommendedVMMonthlyPriceValue ?? item.recommendedMonthlyPriceValue ?? 0);
    const saving = (item.savingCost && item.savingCost > 0) ? parseCost(item.savingCost) : ((item.savingAmount && item.savingAmount > 0) ? parseCost(item.savingAmount) : (current - recommended));
    const rec = resolveAzureRec(item.recommendationType);

    return {
      id: item.id,
      name: item.vmName || item.name || 'Unknown',
      region: item.location || 'Unknown',
      type: 'VM',
      status: item.vmStatus || 'Unknown',
      os: item.osType || '—',
      diskSizeGB: item.diskSizeGB ?? null,
      cpu: parseFloat(item.averageCpuValue ?? item.avgCpuUsage) || null,
      peakCpu: parseFloat(item.peakCpuValue) || null,
      mem: item.memoryGB ?? item.ramGB ?? item.avgMemoryUsage ?? null,
      cost: `$${current.toFixed(2)}`,
      recommendedCost: `$${recommended.toFixed(2)}`,
      saving: formatSavingStr(saving),
      savingValue: saving,
      costValue: current,
      recommendedCostValue: recommended,
      recType: rec.recType,
      recPillClass: rec.recPillClass,
      recommendationRemark: item.remark || buildRecommendationDisplay(item.recommendationType, item.recommendedVMSize ?? item.recommendedSku),
      recLabel: item.recommendationDetail || rec.recLabel,
      actionLabel: 'Assist with AI',
      actionClass: 'mini-btn ai-assist',
      resourceGroup: item.resourceGroup,
      sku: item.currentSku || item.currentVMSize || item.vmSize || item.skuName,
      utilization: item.avgCpuUsage || item.averageCpuValue || item.peakCpuValue,
      tags: item.Tags || item.tags || [],
      checked: false,
      payableMonthlyCost: item.recommendedVMMonthlyPriceValue ?? item.recommendedMonthlyPriceValue ?? null,
    };
  });

// ── VM Table Columns ───────────────────────────────────────────
const vmColumns = [
  { key: 'id', label: 'ID', render: (row) => <IdCell row={row} /> },
  { key: 'name', label: 'VM Name', minWidth: 140 },
  { key: 'region', label: 'Location' },
  { key: 'resourceGroup', label: 'Resource Group', minWidth: 140 },
  { key: 'os', label: 'Operating System', render: (row) => <OsCell row={row} /> },
  { key: 'sku', label: 'VM Size' },
  { key: 'diskSizeGB', label: 'Disk Size (GB)', className: 'num', render: (row) => row.diskSizeGB != null ? <span>{row.diskSizeGB}</span> : <span style={{ color: 'var(--muted)' }}>—</span> },
  { key: 'status', label: 'Status', render: (row) => <StatusCell row={row} /> },
  { key: 'cpu', label: 'CPU Avg (14d)', render: (row) => <UtilBar avgValue={row.cpu} peakValue={row.peakCpu} /> },
  { key: 'cost', label: 'Monthly Cost', className: 'num', render: (row) => <CostCell row={row} /> },
  { key: 'recommendationRemark', label: 'Recommendation', minWidth: 160, render: (row) => <RecCell row={row} /> },
  { key: 'saving', label: 'Monthly Savings', className: 'num', render: (row) => <SavingCell row={row} /> },
  { key: 'recommendedCost', label: 'Payable Cost / Mo', className: 'num', render: (row) => <RecommendedCostCell row={row} /> }, 

  { key: 'action', label: 'Action', render: (row) => <button className={row.actionClass} onClick={row.onAction ? () => row.onAction(row) : undefined}>{row.actionLabel}</button> },
];

// ── Data Builders ─────────────────────────────────────────
const buildKpiData = (totalMonthlyCost, count) => {
  const current = parseCost(totalMonthlyCost?.currentCost ?? 0);
  const recommended = parseCost(totalMonthlyCost?.recommendedCost ?? 0);
  const saving = parseCost(totalMonthlyCost?.savingCost ?? 0);

  return buildBaseCostKpis({
    countTitle: 'Total VMs',
    countValue: count,
    countSubtitle: 'virtual machines',
    current,
    recommended,
    saving,
  });
};

const buildByTypeChartData = (rows) => {
  if (!rows.length) return null;
  const typeMap = {};
  rows.forEach((r) => {
    const sku = r.sku || 'Unknown';
    typeMap[sku] = (typeMap[sku] || 0) + r.costValue;
  });

  const sorted = Object.entries(typeMap).sort((a, b) => b[1] - a[1]).slice(0, 6);
  if (!sorted.reduce((s, [, v]) => s + v, 0)) return null;
  return {
    labels: sorted.map(([t]) => t),
    datasets: [{ data: sorted.map(([, c]) => parseFloat(c.toFixed(2))), backgroundColor: '#f57c00', borderRadius: 4, barPercentage: 0.6 }],
  };
};

const buildTrendChartData = (rows) => {
  const withCpu = rows
    .map((r) => ({ name: r.name, cpu: parseFloat(r.cpu) || 0, cost: r.costValue || 0 }))
    .filter((r) => r.cost > 0)
    .sort((a, b) => b.cost - a.cost)
    .slice(0, 20);
  if (!withCpu.length) return null;
  return {
    labels: withCpu.map((r) => r.name),
    datasets: [
      { label: 'Avg CPU %', data: withCpu.map((r) => r.cpu), backgroundColor: 'rgba(33,150,243,0.7)', borderColor: '#2196f3', borderWidth: 1, borderRadius: 4, yAxisID: 'y1' },
      { label: 'Monthly cost ($)', data: withCpu.map((r) => r.cost), backgroundColor: 'rgba(245,124,0,0.7)', borderColor: '#f57c00', borderWidth: 1, borderRadius: 4, yAxisID: 'y2' },
    ],
  };
};

const buildRightsizingChartData = (rows) => {
  if (!rows.length) return null;

  const buckets = { '< 10%': 0, '10–30%': 0, '30–50%': 0, '50–70%': 0, '70–90%': 0, '> 90%': 0 };
  rows.forEach(({ cpu }) => {
    if (cpu === null) return;
    if (cpu < 10) buckets['< 10%']++;
    else if (cpu < 30) buckets['10–30%']++;
    else if (cpu < 50) buckets['30–50%']++;
    else if (cpu < 70) buckets['50–70%']++;
    else if (cpu < 90) buckets['70–90%']++;
    else buckets['> 90%']++;
  });
  if (!Object.values(buckets).reduce((s, v) => s + v, 0)) return null;
  return {
    labels: Object.keys(buckets),
    datasets: [{ data: Object.values(buckets), backgroundColor: ['#ef4444', '#f57c00', '#fbc02d', '#2ea66f', '#1e88e5', '#ef4444'], borderRadius: 4, barPercentage: 0.7 }],
  };
};

const buildSavingsByActionData = (rows) => {
  if (!rows.length) return null;

  const actionMap = {};
  rows.forEach((r) => {
    const saving = parseSaving(r.saving);
    if (saving <= 0) return;

    const recType = (r.recType || 'Unknown').toUpperCase().trim();
    let actionLabel = 'Other';

    if (recType === 'RIGHTSIZE' || recType === 'DOWNSIZE') {
      actionLabel = 'Rightsize VMs';
    } else if (recType === 'TERMINATE' || recType === 'DELETE' || recType === 'IDLE' || recType === 'UNATTACHED') {
      actionLabel = 'Deallocate idle';
    } else if (recType === 'BUY-RI' || recType === 'RESERVED') {
      actionLabel = 'Buy Reserved Instances';
    }

    actionMap[actionLabel] = (actionMap[actionLabel] || 0) + saving;
  });

  const sorted = Object.entries(actionMap).sort((a, b) => b[1] - a[1]);
  if (!sorted.length) return null;

  const colorMap = {
    'Rightsize VMs': '#f57c00',
    'Deallocate idle': '#ef4444',
    'Buy Reserved Instances': '#2196f3',
    'Other': '#9e9e9e',
  };

  return {
    labels: sorted.map(([t]) => t),
    datasets: [{ data: sorted.map(([, c]) => parseFloat(c.toFixed(2))), backgroundColor: sorted.map(([t]) => colorMap[t] || '#9e9e9e'), borderRadius: 4, barPercentage: 0.6 }],
  };
};

const buildInsightActions = (rows) => {
  const seen = new Set();
  const withSaving = rows
    .filter((r) => {
      const key = `${r.id}|${r.recType}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return parseSaving(r.saving) > 0;
    })
    .map((r) => ({ ...r, savingNum: parseSaving(r.saving) }))
    .sort((a, b) => b.savingNum - a.savingNum);
  const totalSaving = withSaving.reduce((s, r) => s + r.savingNum, 0);
  const actions = withSaving.map((r, i) => {
    let actionLabel = 'Review →';
    let actionClass = 'mini-btn';
    if (r.recType === 'DELETE' || r.recType === 'TERMINATE') { actionLabel = 'Delete →'; actionClass = 'mini-btn ghost'; }
    else if (r.recType === 'RIGHTSIZE' || r.recType === 'DOWNSIZE') actionLabel = 'Resize →';
    return { id: i + 1, title: r.name || r.id, amount: r.saving, desc: `${r.type} · ${r.recommendationRemark}`, actionLabel, actionClass, row: r };
  });
  return { actions, totalSaving };
};

const VM_TABS = [
  { label: 'Combined Recommendations' },
  { label: 'CloudsAnalytics Recommendations' },
  { label: 'Azure Recommendations' },
];

// ── Main VM Optimization Component ───────────────────────────────────
export default function VirtualMachine() {
  const { selectedAccount, userDetails, refetchUserDetails } = useAuth();
  const { getScopedItem } = useScopedLocalStorage();
  const navigate = useNavigate();
  const location = useLocation();

  const userId = userDetails?.userId || '1';

  const [activeTab, setActiveTab] = useState(0);

  const [isLoadingCombined, setIsLoadingCombined] = useState(false);
  const [isLoadingCustom, setIsLoadingCustom] = useState(false);
  const [isLoadingAzure, setIsLoadingAzure] = useState(false);

  const [combinedRows, setCombinedRows] = useState([]);
  const [customRows, setCustomRows] = useState([]);
  const [azureRows, setAzureRows] = useState([]);

  const [combinedKpi, setCombinedKpi] = useState([]);
  const [customKpi, setCustomKpi] = useState([]);
  const [azureKpi, setAzureKpi] = useState([]);

  const [byRegionData, setByRegionData] = useState(null);
  const [byTypeData, setByTypeData] = useState(null);
  const [byActionData, setByActionData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [rightsizingData, setRightsizingData] = useState(null);
  const [insightActions, setInsightActions] = useState([]);
  const [insightTotal, setInsightTotal] = useState(0);

  const [tagList, setTagList] = useState([]);
  const [filterList, setFilterList] = useState({ tag: '', values: [] });

  const [aiOpen, setAiOpen] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiData, setAiData] = useState(null);

  const [snackbarMessage, setSnackbarMessage] = useState(null);
  const [snackbarStatus, setSnackbarStatus] = useState(null);
  const [showActivateNowConfirmModal, setShowActivateNowConfirmModal] = useState(false);

  const fetchedTabsRef = useRef(new Set());

  // ── Tag Fetching ───────────────────────────────────────────────
  const fetchAzureTagList = useCallback(() => {
    DebugService.log('VM Optimization: Fetching filter tag list');

    setTagList([]);
    setFilterList({ tag: '', values: [] });

    if (!selectedAccount) return;

    const dataRequest = {
      type: RESOURCE_API_TYPE,
      userAccountId: selectedAccount
    };

    InventoryApiService.getAzureFilterTagList(APIConstants.ENDPOINT_AZURE_FILTER_TAG, dataRequest)
      .then(res => {
        DebugService.log('VM Optimization: Filter tag list response', res);
        setTagList(res?.data ?? []);
      })
      .catch(err => {
        DebugService.error('VM Optimization: Error while fetching tag list', err);
      });
  }, [selectedAccount]);

  // ── Data Fetching Logic ───────────────────────────────────────────────
  const fetchTabData = useCallback((tabIndex, isForceRefresh = false) => {
    if (!selectedAccount) return;
    if (!isForceRefresh && fetchedTabsRef.current.has(tabIndex)) return;
    fetchedTabsRef.current.add(tabIndex);

    const applyTrialLimit = (data) => {
      const subscriptionStatus = userDetails?.subscriptionStatus || getScopedItem(STORAGE_KEYS.SUBSCRIPTION_STATUS);
      if (subscriptionStatus === SUBSCRIPTION_STATUS.TRIAL_ACTIVE) {
        return data.slice(0, TRIAL_SUBSCRIPTION_DATA_LIMIT);
      }
      return data;
    };

    if (tabIndex === 0) {
      setIsLoadingCombined(true);
      setCombinedRows([]);
      AzureOptimizationService.getTabularData({
        userId: userId,
        type: 'COMBINED_' + RESOURCE_API_TYPE,
        userAccountId: selectedAccount,
      })
      .then((data) => {
          const mappedRows = mapCombinedRows(applyTrialLimit(data?.rows ?? []));
          setCombinedRows(mappedRows);
          setCombinedKpi(buildKpiData(data.totalMonthlyCost, data?.rows?.length ?? 0));
        })
        .catch((error) => {
          DebugService.error('VM Optimization: Error fetching combined data', error);
        })
        .finally(() => setIsLoadingCombined(false));
    }

    if (tabIndex === 1) {
      setIsLoadingCustom(true);
      setCustomRows([]);

      AzureOptimizationService.getTabularData({
        userId: userId,
        type: 'CUSTOM_' + RESOURCE_API_TYPE,
        userAccountId: selectedAccount,
      })
      .then((data) => {
          const mappedRows = mapCustomRows(applyTrialLimit(data?.rows ?? []));
          setCustomRows(mappedRows);
          setCustomKpi(buildKpiData(data.totalMonthlyCost, data?.rows?.length ?? 0));
        })
        .catch((error) => {
          DebugService.error('VM Optimization: Error fetching custom data', error);
        })
        .finally(() => setIsLoadingCustom(false));
    }

    if (tabIndex === 2) {
      setIsLoadingAzure(true);
      setAzureRows([]);

      AzureOptimizationService.getTabularData({
        userId: userId,
        type: RESOURCE_API_TYPE,
        userAccountId: selectedAccount,
      })
        .then((data) => {
          const mappedRows = mapAzureRows(applyTrialLimit(data?.rows ?? []));
          setAzureRows(mappedRows);
          setAzureKpi(buildKpiData(data.totalMonthlyCost, data?.rows?.length ?? 0));
        })
        .catch((error) => {
          DebugService.error('VM Optimization: Error fetching azure data', error);
        })
        .finally(() => setIsLoadingAzure(false));
    }
  }, [selectedAccount, userId, userDetails, getScopedItem]);

  // ── Event Handlers ───────────────────────────────────────────────────
  const handleTabChange = (i) => {
    setActiveTab(i);
    fetchTabData(i);
  };

  const handleFilterChange = (field, value) => {
    if (field === 'tag') {
      setFilterList({ tag: value ?? '', values: [] });
      return;
    }
    if (field === 'values') {
      const values = typeof value === 'string' ? value.split(',') : (value ?? []);
      setFilterList(prev => ({ tag: prev.tag, values }));
    }
  };

  const handleDetailsClick = (row) => {
    setAiData({
      resourceId: row.id,
      resourceName: row.name,
      resourceType: row.type,
      status: row.status,
      region: row.region,
      cpuAvg: row.cpu,
      currentMonthlyCost: row.cost,
      recommendation: row.recType,
      recommendedType: row.recLabel,
      estimatedMonthlySaving: row.saving,
      resourceGroup: row.resourceGroup,
      sku: row.sku,
      utilization: row.utilization,
    });
    setAiPrompt(
      'You are a cloud cost advisor. Based on the data provided, give a short and natural insight ' +
      'as if you are speaking directly to the user — not reading a report. ' +
      'Focus on actionable advice for this VM optimization opportunity.'
    );
    setAiOpen(true);
  };

  const handleExport = () => {
    const activeTabName = VM_TABS[activeTab]?.label?.toLowerCase().replace(/\s+/g, '_') || 'unknown';
    exportCSV(
      activeRows,
      vmColumns.filter((c) => !['cpu', 'action'].includes(c.key)),
      buildExportFilename(`vm_optimization_${activeTabName}`, selectedAccount)
    );
  };

  const activateSubscription = useCallback(() => {
    setSnackbarStatus(null);
    setSnackbarMessage(null);

    const userSubscriptionId = userDetails?.userSubscriptionId;
    if (userSubscriptionId) {
      const storedUserDetails = getScopedItem(STORAGE_KEYS.LOCAL_USER_DETAILS);
      const parsedUserDetails = JSON.parse(storedUserDetails);

      const requestBody = {
        userId: parsedUserDetails?.userId,
        accountId: parsedUserDetails?.userAccounts?.[0]?.accountId,
        userSubscriptionId
      };

      DebugService.log('VM Optimization: Activate subscription request initiated', requestBody);

      SubscriptionApiService.activateSubscription(requestBody)
        .then(res => {
          DebugService.log('VM Optimization: Activate subscription response', res);
          if (res.data) {
            setSnackbarStatus('success');
            setSnackbarMessage(`${res.data.message}. Reload your page`);
            refetchUserDetails();
          } else {
            if (res.error.code === 525) {
              setScopedItem(STORAGE_KEYS.CURRENT_ROUTE, location.pathname);
              navigate('/pricing');
            } else {
              setSnackbarStatus('failed');
              setSnackbarMessage(res.error.message ?? 'Subscription activation failed');
            }
          }
        })
        .catch(error => {
          DebugService.error('VM Optimization: Plan purchase failed', error);
          setSnackbarStatus('failed');
          setSnackbarMessage('Something went wrong');
        });
    }
  }, [userDetails, getScopedItem, navigate, refetchUserDetails, location.pathname]);

  const activateNowConfirmationHandler = (isConfirm) => {
    if (isConfirm) activateSubscription();
    setShowActivateNowConfirmModal(false);
  };

  // ── Data Processing ───────────────────────────────────────────────────
  const filteredCustomRows = useMemo(() => {
    if (!filterList.tag || filterList.values.length === 0) return customRows;
    return customRows.filter((row) => {
      const tags = row.tags || [];
      return tags.some((t) => (t.Key || t.key) === filterList.tag && filterList.values.includes(t.Value || t.value));
    });
  }, [customRows, filterList]);

  const activeRows = useMemo(() => {
    let rows;
    if (activeTab === 0) rows = combinedRows;
    else if (activeTab === 1) rows = filteredCustomRows;
    else rows = azureRows;

    return rows.map((row) => ({ ...row, onAction: handleDetailsClick }));
  }, [activeTab, combinedRows, filteredCustomRows, azureRows]);

  useEffect(() => {
    if (activeRows.length > 0) {
      setByRegionData(buildGroupedCostDonut(activeRows, 'region'));
      setByTypeData(buildByTypeChartData(activeRows));
      setByActionData(buildSavingsByActionData(activeRows));
      setTrendData(buildTrendChartData(activeRows));
      setRightsizingData(buildRightsizingChartData(activeRows));
      const { actions, totalSaving } = buildInsightActions(activeRows);
      setInsightActions(actions);
      setInsightTotal(totalSaving);
    } else {
      setByRegionData(null);
      setByTypeData(null);
      setByActionData(null);
      setTrendData(null);
      setRightsizingData(null);
      setInsightActions([]);
      setInsightTotal(0);
    }
  }, [activeRows]);

  const activeKpi = useMemo(() => {
    if (activeTab === 0) return combinedKpi;
    if (activeTab === 1) return customKpi;
    return azureKpi;
  }, [activeTab, combinedKpi, customKpi, azureKpi]);

  const isLoadingActive = useMemo(() => {
    if (activeTab === 0) return isLoadingCombined;
    if (activeTab === 1) return isLoadingCustom;
    return isLoadingAzure;
  }, [activeTab, isLoadingCombined, isLoadingCustom, isLoadingAzure]);

  // Keep AI chatbot context in sync with the data shown on this page
  useEffect(() => {
    setScopedItem(STORAGE_KEYS.AI_CHATBOT_REQUEST_DATA, {
      page: 'Virtual Machine Optimization',
      kpi: activeKpi,
      vmRecommendations: activeRows,
    });
  }, [activeRows, activeKpi]);

  const rightsizingCandidateCount = activeRows.filter((r) => r.cpu !== null && r.cpu < 30 && r.status === 'Running').length;

  // ── Effects ───────────────────────────────────────────────────────
  useEffect(() => {
    if (selectedAccount && selectedAccount !== ADD_NEW_ACCOUNT) {
      setFilterList({ tag: '', values: [] });
      fetchedTabsRef.current = new Set();
      fetchTabData(0);
      fetchAzureTagList();
    } else {
      setIsLoadingCombined(false);
      setIsLoadingCustom(false);
      setIsLoadingAzure(false);
    }
  }, [selectedAccount, fetchTabData, fetchAzureTagList]);

  useEffect(() => {
    if (activeTab !== 1) return;
    if (filterList.tag === '' || (filterList.tag !== '' && filterList.values.length > 0)) {
      // Filter is handled by useMemo — no refetch needed for Custom tab tag filtering
    }
  }, [filterList, activeTab]);

  // ── Subscription Gating Component ─────────────────────────────────────
  const SubscribeOverlay = () => {
    const [message, setMessage] = useState('');
    const [btnTitle, setBtnTitle] = useState('');
    const [showBtn, setShowBtn] = useState(false);

    useEffect(() => {
      const subscriptionStatus = userDetails?.subscriptionStatus;
      if ([SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.TRIAL_ACTIVE].includes(subscriptionStatus)) {
        setBtnTitle('');
        setShowBtn(false);
        setMessage('No Recommendation(s)');
      } else {
        setMessage('Your subscription is not active');
        setBtnTitle('Activate Now');
        setShowBtn(true);
      }
    }, [userDetails]);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
        <Typography variant="h6" color="textSecondary" className="activate-subscription-text">
          {message}
          <br />
          {showBtn && (
            <Button variant="contained" size="small" className="activate-button" title="Click to activate" onClick={() => setShowActivateNowConfirmModal(true)}>
              {btnTitle}
            </Button>
          )}
        </Typography>
      </Box>
    );
  };

  return (
    <>
      <div className="breadcrumb">
        Optimization <span style={{ color: 'var(--muted-2)' }}>›</span> <strong>Virtual Machines</strong>
      </div>

      <div className="page-head">
        <div>
          <h1>
            VM Optimization{' '}
            {isLoadingActive ? <BadgeSkeleton /> : <span className="badge">{activeRows.length} VMs</span>}
          </h1>
          <div className="meta">View, rightsize, and act on recommendations across your Virtual Machines</div>
        </div>
        <div className="head-actions">
          <button className="btn ghost" onClick={handleExport} disabled={activeRows.length === 0}>⬇ Export</button>
          <button className="btn ghost" onClick={() => fetchTabData(activeTab, true)}>↻ Refresh</button>
        </div>
      </div>

      <KpiRow kpiData={activeKpi} loading={isLoadingActive} />

      <div className="grid-3" style={{ marginTop: '14px' }}>
        <Card className="first" title="CPU vs Cost — Active VMs" subtitle="Avg CPU % vs monthly cost · top 20 by cost">
          <LineChart data={trendData} loading={isLoadingActive} />
        </Card>
        <Card className="first" title="Rightsizing opportunities" subtitle={`By utilization bucket · ${rightsizingCandidateCount} candidates`}>
          <VerticalBarChart data={rightsizingData} xAxisLabel="CPU avg" loading={isLoadingActive} yAxisSuffix="VMs" />
        </Card>
        <TopActionsCard title="Top VM actions" actions={insightActions} total={insightTotal} loading={isLoadingActive} entityName="VMs" onAction={(item) => item.row && handleDetailsClick(item.row)} />
      </div>

      <Tabs tabs={VM_TABS} defaultActive={0} onTabChange={handleTabChange} />

      {activeTab === 1 && (
        <div className="filter-bar">
          <div className="field">
            <label>Filter Tag</label>
            <select value={filterList.tag} onChange={(e) => handleFilterChange('tag', e.target.value)}>
              <option value="">None</option>
              {tagList.map((tag) => <option key={tag?.Key} value={tag?.Key}>{tag?.Key}</option>)}
            </select>
          </div>
          <div className="field">
            <label>Filter Tag Values</label>
            <MultiSelect
              options={tagList.find((t) => t?.Key === filterList.tag)?.Value ?? []}
              value={filterList.values}
              onChange={(vals) => handleFilterChange('values', vals)}
              disabled={!filterList.tag}
            />
          </div>
          <div className="spacer" />
          <button className="btn ghost" onClick={() => setFilterList({ tag: '', values: [] })}>✕ Clear</button>
        </div>
      )}

      <Table
        rows={activeRows}
        columns={vmColumns}
        loading={isLoadingActive}
        skeletonRows={8}
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={10}
        defaultSortKey="saving"
        defaultSortDir="desc"
        onExport={handleExport}
        emptyText="No VMs found."
        subscriptionGated
      />

      <div className="grid-2" style={{ marginTop: '14px' }}>
        <Card className="first" title="Cost by VM Size" subtitle="Top 6 VM families this month">
          <HorizontalBarChart data={byTypeData} loading={isLoadingActive} />
        </Card>
        <Card className="first" title="Recommended savings by action" subtitle="Where the savings come from">
          <HorizontalBarChart data={byActionData} loading={isLoadingActive} />
        </Card>
      </div>

      <AIModal
        open={aiOpen}
        onClose={() => { setAiOpen(false); setAiPrompt(''); setAiData(null); }}
        prompt={aiPrompt}
        data={aiData}
      />

      <SnackbarNotification
        message={snackbarMessage}
        status={snackbarStatus}
        onClose={() => { setSnackbarMessage(null); setSnackbarStatus(null); }}
      />

      <ConfirmModal
        open={showActivateNowConfirmModal}
        title="Activate Subscription"
        message="Do you want to activate your subscription now?"
        onConfirm={() => activateNowConfirmationHandler(true)}
        onCancel={() => activateNowConfirmationHandler(false)}
      />
    </>
  );
}